// apps/web/src/pages/DashboardPage.tsx
//
// KAN-209 — Briefing Desk dashboard (Variant A of the redesign).
// Composition, top to bottom:
//   header        — title, subtitle, External Intelligence pill,
//                   refreshed stamp, ⌘K search affordance
//   highlights    — 4 editorial cells derived from T1/T4/T6 + top story
//   pulse         — all 8 dashboard concepts as one compact strip
//   detail        — the existing T1–T8 tiles, collapsible
//   feed + rail   — ranked story feed with filters; rising signals,
//                   regulatory watch, victim countries
//
// Help affordances funnel into the existing HelpDrawer via tile ids
// t1..t8 (HELP_ENTRIES below). Data comes exclusively from existing
// endpoints: /api/dashboard/* tiles and GET /stories.

import { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { Search } from 'lucide-react'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { DashboardHighlightsBand } from '@/components/dashboard/DashboardHighlightsBand'
import { OperationalPulseStrip } from '@/components/dashboard/OperationalPulseStrip'
import { OperationalDetailGrid } from '@/components/dashboard/OperationalDetailGrid'
import { TopIntelligenceFeed } from '@/components/dashboard/TopIntelligenceFeed'
import { DashboardRail } from '@/components/dashboard/DashboardRail'
import {
  HelpDrawer,
  type HelpDrawerEntry,
} from '@/components/dashboard/HelpDrawer'
import { PrefsFilterChip } from '@/components/dashboard/PrefsFilterChip'
import { useDashboardPrefsCounts } from '@/hooks/useDashboardPrefsCounts'
import { useStories } from '@/hooks/stories'

const HELP_ENTRIES: Record<string, HelpDrawerEntry> = {
  t1: {
    id: 't1',
    title: 'Criticals 24h',
    whatItShows:
      'Stories with severity = critical from the last 24 hours, compared with the 7-day average.',
    whatYouDecide:
      'Whether the operational tempo justifies an after-hours review.',
    dataSource:
      "stories WHERE max_severity = 'critical' AND earliest_published_at >= now() - 24h",
    tierDefinitions: [
      { label: 'critical', meaning: 'Story severity classified as critical.' },
    ],
  },
  t2: {
    id: 't2',
    title: 'Ransom claims 7d',
    whatItShows:
      'Leak-site claims posted in the last 7 days, with the top 3 (country, sector) buckets. These are VICTIM countries / sectors — where the targeted org sits — not actor origin.',
    whatYouDecide:
      'Whether ransomware activity in your sector or geography warrants an internal advisory.',
    dataSource:
      'leak_site_claims WHERE claim_date >= now() - 7d AND NOT suppressed',
  },
  t3: {
    id: 't3',
    title: 'Patches due',
    whatItShows:
      'Critical vulnerabilities updated in the last 7 days: KEV-tagged, actively exploited, or CVSS ≥ 9.0.',
    whatYouDecide: 'Which patches need to land this week.',
    dataSource:
      'vulnerabilities WHERE last_updated >= now() - 7d AND (KEV active OR exploitation_status IN (active, widespread) OR cvss_score >= 9.0)',
    tierDefinitions: [
      {
        label: 'KEV',
        meaning:
          'CISA Known Exploited Vulnerabilities catalog — currently listed.',
      },
      {
        label: 'exploited',
        meaning: 'Active or widespread exploitation reported.',
      },
      {
        label: 'high',
        meaning: 'CVSS ≥ 9.0 but not (yet) KEV or exploited.',
      },
    ],
  },
  t4: {
    id: 't4',
    title: 'New fines 30d',
    whatItShows:
      'Regulatory actions decided in the last 30 days, scoped to your jurisdictions when prefs apply, with the largest single action highlighted.',
    whatYouDecide: 'Whether peer enforcement trends warrant a control review.',
    dataSource:
      "regulatory_actions WHERE decision_date >= now() - 30d AND details->>'fineAmount' IS NOT NULL",
  },
  t5: {
    id: 't5',
    title: 'My sector heat',
    whatItShows:
      '7-day high+critical story volume in your sectors vs the 30-day baseline (÷ 30 × 7). Without prefs: global aggregate.',
    whatYouDecide: 'Whether your sector is unusually noisy this week.',
    dataSource: 'stories severity-by-sector ratio (7d ÷ baseline)',
    tierDefinitions: [
      {
        label: 'High',
        meaning: 'Ratio > 1.5× the baseline — elevated activity.',
      },
      {
        label: 'Elevated',
        meaning: 'Ratio > 1.0× — slightly above baseline.',
      },
      { label: 'Normal', meaning: 'Ratio ≤ 1.0× — at or below baseline.' },
    ],
  },
  t6: {
    id: 't6',
    title: 'Supply-chain 7d',
    whatItShows:
      'Supply-chain incidents (cloud + IdP + OSV-without-CVE advisories) published in the last 7 days, by severity.',
    whatYouDecide:
      'Whether a provider you depend on has degraded security posture.',
    dataSource: 'supply_chain_incidents WHERE published_at >= now() - 7d',
  },
  t7: {
    id: 't7',
    title: 'Top victim countries 30d',
    whatItShows:
      'Top countries hit by ransomware claims in the last 30 days. Renders only when the data is statistically sufficient (coverage ≥ 40%, ≥ 5 distinct countries, ≥ 50 eligible rows). When insufficient, falls back to top victim sectors.',
    whatYouDecide:
      'Whether to escalate physical-security or geopolitical concerns to specific subsidiaries.',
    dataSource: 'leak_site_claims.victim_country (with §2.2 sufficiency gate)',
    tierDefinitions: [
      {
        label: 'sufficient',
        meaning:
          'coverageRatio ≥ 0.40 AND distinctBuckets ≥ 5 AND totalRows ≥ 50.',
      },
      {
        label: 'fallback',
        meaning: 'When insufficient, top victim sectors are shown instead.',
      },
    ],
  },
  t8: {
    id: 't8',
    title: 'Regulatory watch',
    whatItShows:
      'Watched regulatory frameworks and upcoming deadline signals. Without watches, shows the next published regulatory deadline or setup CTA.',
    whatYouDecide:
      'Whether a regulatory calendar item needs review or whether to configure framework watches.',
    dataSource:
      'user_framework_watches + regulatory_deadlines for published full-coverage frameworks',
    tierDefinitions: [
      {
        label: 'personalised',
        meaning: 'You have watched full-coverage frameworks with published briefs.',
      },
      {
        label: 'default',
        meaning: 'No qualifying watches; shows global full-coverage framework deadlines.',
      },
      {
        label: 'cta',
        meaning: 'No qualifying watches or upcoming deadlines; prompts watch setup.',
      },
    ],
  },
}

const FEED_WINDOW_DAYS = 7
const FEED_FETCH_LIMIT = 30
const FEED_WINDOW_REFRESH_MS = 60_000

function feedWindowStartIso() {
  return new Date(Date.now() - FEED_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString()
}

function useRollingFeedFrom() {
  const [feedFrom, setFeedFrom] = useState(feedWindowStartIso)

  useEffect(() => {
    const id = window.setInterval(() => {
      setFeedFrom(feedWindowStartIso())
    }, FEED_WINDOW_REFRESH_MS)
    return () => window.clearInterval(id)
  }, [])

  return feedFrom
}

function openCommandPalette() {
  document.dispatchEvent(new CustomEvent('intelhub:open-command-palette'))
}

export function DashboardPage() {
  const [activeHelpId, setActiveHelpId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(true)
  const { sectorCount, countryCount, vendorCount, clearTemporary } =
    useDashboardPrefsCounts()

  const feedFrom = useRollingFeedFrom()
  const storiesQuery = useStories({ from: feedFrom, limit: FEED_FETCH_LIMIT })
  const stories = storiesQuery.data?.stories

  const handleHelpClick = (id: string) => setActiveHelpId(id)
  const handleCloseHelp = () => setActiveHelpId(null)

  const activeEntry = useMemo(
    () => (activeHelpId ? (HELP_ENTRIES[activeHelpId] ?? null) : null),
    [activeHelpId],
  )

  const refreshedAt =
    storiesQuery.dataUpdatedAt > 0 ? new Date(storiesQuery.dataUpdatedAt) : null

  return (
    <div className="mx-auto flex max-w-[1440px] flex-col gap-7 p-4 md:p-6">
      <header className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-light tracking-tight">
            Intelligence Dashboard
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            External cyber intelligence, ranked by operational relevance.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
            <span
              className="h-1.5 w-1.5 rounded-full bg-success"
              aria-hidden="true"
            />
            External Intelligence
          </span>
          {refreshedAt ? (
            <span
              className="whitespace-nowrap font-mono text-[11px] text-muted-foreground"
              title={refreshedAt.toISOString()}
            >
              Refreshed {format(refreshedAt, 'HH:mm')}
            </span>
          ) : null}
          <button
            type="button"
            onClick={openCommandPalette}
            className="inline-flex min-w-[200px] items-center gap-2 rounded-md border border-border bg-secondary px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Search className="h-3.5 w-3.5" aria-hidden="true" />
            Search intelligence…
            <kbd className="ml-auto rounded-sm border border-border px-1.5 py-0.5 font-mono text-[10px]">
              ⌘K
            </kbd>
          </button>
        </div>
      </header>

      <PrefsFilterChip
        sectorCount={sectorCount}
        countryCount={countryCount}
        vendorCount={vendorCount}
        onClear={clearTemporary}
      />

      <section aria-labelledby="dash-highlights" className="flex flex-col gap-3">
        <DashboardSectionHeader
          headingId="dash-highlights"
          label="Today's highlights"
          aux={format(new Date(), 'EEE d MMM yyyy')}
        />
        <DashboardHighlightsBand
          stories={stories}
          storiesLoading={storiesQuery.isLoading}
          storiesError={storiesQuery.isError}
        />
      </section>

      <section aria-labelledby="dash-pulse" className="flex flex-col gap-3">
        <DashboardSectionHeader
          headingId="dash-pulse"
          label="Operational pulse"
          aux="Use a metric's ? for its definition and source"
        />
        <OperationalPulseStrip onHelpClick={handleHelpClick} />
      </section>

      <section aria-labelledby="dash-detail" className="flex flex-col gap-3">
        <DashboardSectionHeader
          headingId="dash-detail"
          label="Operational detail"
          collapsible
          open={detailOpen}
          onToggle={() => setDetailOpen((o) => !o)}
          contentId="dash-detail-grid"
          aux={detailOpen ? null : '8 widgets hidden'}
        />
        <div id="dash-detail-grid">
          {detailOpen ? (
            <OperationalDetailGrid onHelpClick={handleHelpClick} />
          ) : null}
        </div>
      </section>

      <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section aria-labelledby="dash-feed" className="flex flex-col gap-3">
          <DashboardSectionHeader
            headingId="dash-feed"
            label="Top intelligence"
            aux={
              stories && stories.length > 0
                ? `${stories.length} stories · last ${FEED_WINDOW_DAYS} days`
                : null
            }
          />
          <TopIntelligenceFeed
            stories={stories}
            isLoading={storiesQuery.isLoading}
            isError={storiesQuery.isError}
            onRetry={() => storiesQuery.refetch()}
          />
        </section>
        <DashboardRail
          stories={stories}
          storiesLoading={storiesQuery.isLoading}
          storiesError={storiesQuery.isError}
        />
      </div>

      <HelpDrawer
        activeTileId={activeHelpId}
        entry={activeEntry}
        onClose={handleCloseHelp}
      />
    </div>
  )
}
